import { lazy, Suspense, type ReactNode } from "react";
import type { TopicId } from "../types/dashboard";
import { AdminUsersView } from "../cards/AdminUsersView";
import { DevopsView } from "../cards/DevopsView";
import { FcrCoreView } from "../cards/FcrCoreView";
import { LbView } from "../cards/LbView";
import { ConnectedChargersRow } from "../cards/ConnectedChargersRow";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { DASHBOARD_FREQ_RANGES } from "../context/FreqRangeContext";

const FreqHeroChart = lazy(() =>
  import("../cards/FreqHeroChart").then((m) => ({ default: m.FreqHeroChart })),
);
const WsConnectionsChart = lazy(() =>
  import("../cards/WsConnectionsChart").then((m) => ({ default: m.WsConnectionsChart })),
);
const OcppTrafficCharts = lazy(() =>
  import("../cards/OcppTrafficCharts").then((m) => ({ default: m.OcppTrafficCharts })),
);
const PerfMonitorView = lazy(() =>
  import("../cards/PerfMonitorView").then((m) => ({ default: m.PerfMonitorView })),
);
const ChargerPowerChart = lazy(() =>
  import("../cards/ChargerPowerChart").then((m) => ({ default: m.ChargerPowerChart })),
);
const RegionalMetersView = lazy(() =>
  import("../cards/RegionalMetersView").then((m) => ({ default: m.RegionalMetersView })),
);
const FreqRibbonView = lazy(() =>
  import("../cards/FreqRibbonView").then((m) => ({ default: m.FreqRibbonView })),
);
const FrequencyTopicView = lazy(() =>
  import("../cards/FrequencyTopicView").then((m) => ({ default: m.FrequencyTopicView })),
);

function ChartFallback() {
  return (
    <div
      className="flex h-48 items-center justify-center rounded-lg border border-border-subtle bg-card text-text-secondary"
      style={{ fontSize: "var(--font-body)" }}
    >
      Loading chart…
    </div>
  );
}

function withSuspense(node: ReactNode) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<ChartFallback />}>{node}</Suspense>
    </ErrorBoundary>
  );
}

export type CardType = "hero" | "chart" | "tile-row" | "health-bar" | "placeholder";

export type CardDefinition = {
  id: string;
  topic: TopicId;
  row: string;
  rowLabel?: string;
  type: CardType;
  order: number;
  render: () => ReactNode;
};

export const CARD_REGISTRY: CardDefinition[] = [
  {
    id: "freq-hero",
    topic: "dashboard",
    row: "hero",
    type: "hero",
    order: 1,
    render: () => withSuspense(<FreqHeroChart availableRanges={DASHBOARD_FREQ_RANGES} />),
  },
  {
    id: "ws-chart-dash",
    topic: "dashboard",
    row: "charts",
    type: "chart",
    order: 2,
    render: () => withSuspense(<WsConnectionsChart title="WS connections (ocpp-edge)" />),
  },
  {
    id: "chargers-dash",
    topic: "dashboard",
    row: "tiles",
    rowLabel: "Connected chargers",
    type: "tile-row",
    order: 3,
    render: () => <ConnectedChargersRow />,
  },
  {
    id: "ocpp-ws-hero",
    topic: "ocpp",
    row: "hero",
    type: "hero",
    order: 1,
    render: () => withSuspense(<WsConnectionsChart tall title="WS connections (ocpp-edge)" />),
  },
  {
    id: "ocpp-traffic",
    topic: "ocpp",
    row: "charts",
    rowLabel: "OCPP messages & Ethernet traffic",
    type: "chart",
    order: 2,
    render: () => withSuspense(<OcppTrafficCharts />),
  },
  {
    id: "frequency-layout",
    topic: "frequency",
    row: "hero",
    type: "hero",
    order: 1,
    render: () => withSuspense(<FrequencyTopicView />),
  },
  {
    id: "regional-meters",
    topic: "ecg",
    row: "hero",
    type: "hero",
    order: 1,
    render: () => withSuspense(<RegionalMetersView />),
  },
  {
    id: "freq-ribbon",
    topic: "ecg",
    row: "charts",
    rowLabel: "Ribbon",
    type: "chart",
    order: 2,
    render: () => withSuspense(<FreqRibbonView />),
  },
  {
    id: "charger-power",
    topic: "power",
    row: "hero",
    type: "hero",
    order: 1,
    render: () => withSuspense(<ChargerPowerChart tall />),
  },
  {
    id: "lb-service",
    topic: "fcr",
    row: "lb",
    type: "hero",
    order: 0,
    render: () => <LbView />,
  },
  {
    id: "fcr-core-modules",
    topic: "fcr",
    row: "hero",
    type: "hero",
    order: 1,
    render: () => <FcrCoreView />,
  },
  {
    id: "infra-perf",
    topic: "infra",
    row: "hero",
    type: "hero",
    order: 1,
    render: () => withSuspense(<PerfMonitorView />),
  },
  {
    id: "admin-users",
    topic: "admin",
    row: "hero",
    type: "hero",
    order: 1,
    render: () => <AdminUsersView />,
  },
  {
    id: "devops-main",
    topic: "devops",
    row: "hero",
    type: "hero",
    order: 1,
    render: () => <DevopsView />,
  },
];

export function cardsForTopic(topic: TopicId): CardDefinition[] {
  return CARD_REGISTRY.filter((c) => c.topic === topic).sort((a, b) => a.order - b.order);
}

export function groupCardsByRow(cards: CardDefinition[]) {
  const rows = new Map<string, { label?: string; cards: CardDefinition[] }>();
  for (const card of cards) {
    const entry = rows.get(card.row) ?? { label: card.rowLabel, cards: [] };
    if (card.rowLabel) entry.label = card.rowLabel;
    entry.cards.push(card);
    rows.set(card.row, entry);
  }
  return rows;
}
